import {CUSTOM_EVENTS, VALIDATION_REG_EXP} from '@/const';
import channel from '@/store/channel/channel';
import {HOME_TYPES_NAMES} from '@/ui_builder/homeTypes';
import {eventTeams, eventTimeObject} from '@/league/components/EventCards/eventMethods';
import Vue from 'vue';

/**
 * Get the correct type of constructor
 * @param {constructor} fn
 * @return {string|string}
 */
function getType(fn) {
  const match = fn && fn.toString().match(/^\s*function (\w+)/);
  return match ? match[1] : '';
}

/**
 * Check is both types are same
 * @param {constructor} type - function constructor.
 * @param {constructor} expectedType - name of expected constructor: Array, Number, Object etc.
 * @return {boolean}
 */
export function isSameType(type, expectedType) {
  return getType(type) === getType(expectedType);
}

/**
 * Function to load a script from url.
 * @param {string} url - Download url.
 * @param {function|undefined} cb - onLoad callback.
 * @param {object|undefined} attrs - script attributes
 */
export function loadScript(url, cb, attrs) {
  const tag = document.createElement('script');
  tag.src = url;
  tag.onload = () => cb?.();
  Object.entries(attrs || {}).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
  const firstScriptTag = document.getElementsByTagName('script')[0];
  firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
}

/**
 * Separate abortSignal from request params
 * @param {object} params
 * @return {{token: ({abortSignal}|undefined), props:  object}}
 */
export function ejectAbortSignal(params) {
  let token = undefined;
  if (params.abortSignal) {
    token = params.abortSignal;
    delete params.abortSignal;
  }
  return {
    token,
    params,
  };
}

/**
 * Get from Event Object home team and away team data object
 * @param {object} event
 * @param {Array.<Object>} event.participants
 * @param {String|Number} event.home_team_id
 * @return {{awayTeam: {}, homeTeam: {}}}
 */
export function getAwayHomeTeams({
  participants,
  home_team_id: homeId,
} = {}) {
  const homeTeam = {};
  const awayTeam = {};

  participants?.forEach((team) => {
    let teamObj;
    let defaultImage;
    let defaultColor;
    if (homeId) {
      const isHome = Number(team.id) === Number(homeId);
      teamObj = isHome ? homeTeam : awayTeam;
      defaultImage =
        isHome ? 'generic/team_home.png' : 'generic/team_visitor.png';
      defaultColor = isHome ? '#000000' : null;
    } else {
      teamObj = Object.keys(awayTeam).length ? homeTeam : awayTeam;
      defaultImage = 'generic/team_logo.png';
    }
    const normalizedProperties = {};
    Object.entries(team.properties || {}).forEach(([key, val]) => {
      normalizedProperties[key.toLowerCase()] = val;
    });
    teamObj.name = team.name;
    teamObj.img = team.images || defaultImage;
    teamObj.shortName = team.short_name;
    teamObj.alias = team.alias;
    teamObj.gender = normalizedProperties.gender?.[0];
    teamObj.sport = normalizedProperties.sport?.[0];
    teamObj.division = normalizedProperties.division?.[0];
    teamObj.color = team.color || defaultColor;
    teamObj.id = team.id;
  });

  return {
    homeTeam,
    awayTeam,
  };
}

/**
 * Get regular string and
 * return string consists from substring divided by separator and wrapped to html tag
 * @param {string} str
 * @param {string} separator
 * @param {string} tag
 * @return {string}
 */
export function parseStringBySeparatorToHtml(str, separator = '\r\n', tag = 'p') {
  if (!str) return '';
  const strArr = str.split(separator);
  return strArr.reduce((total, next) => {
    return `${total}<${tag}>${next}</${tag}>`;
  }, '');
}

/**
 * @param {string} email
 * @return {boolean}
 */
export function validateEmail(email) {
  return VALIDATION_REG_EXP.email.test(email) || VALIDATION_REG_EXP.numberEmail.test(email);
}

/**
 * @param {string} name
 * @return {string}
 */
export function teamName(name) {
  return name.replace('University of', '').replace('University', '').trim();
}

/**
 * @param {string} dateString
 * @return {Date}
 */
export function getNewDateFromString(dateString) {
  return new Date(dateString.replace(/-/g, '/'));
}

/**
 * Converting seconds into human-understandable time (HH:mm:ss || mm:ss)
 * @param {Number} seconds
 * @param {Boolean} hoursNeed
 * @return {string}
 */
export function timeToHumanFormat(seconds, hoursNeed = false) {
  const secondsInHour = 3600;
  const withHours = hoursNeed || Boolean(Math.trunc(seconds / secondsInHour));
  const time = new Date(0);
  time.setHours(0, 0, seconds, 0);

  const parsedTime = time.toLocaleString(undefined, {
    second: '2-digit',
    minute: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  });
  const newArr = parsedTime.split(/\D/).reverse();
  newArr.length = withHours ? 3 : 2;
  return newArr.reverse().join(':');
}

/**
 * Download data
 * @param {string} url
 * @param {string} name
 */
export function initDownload(url, name = 'download') {
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.setAttribute('download', name);
  a.click();
  a.remove();
}

/**
 * Parse human-understandable time to seconds
 * @param {string} timeString ( hh:mm:ss )
 * @param {string} separator
 * @return {number} seconds
 */
export function secondsFromString(timeString, separator = ':') {
  const [ss = 0, mm = 0, hh = 0] = timeString?.split?.(separator)?.reverse?.();
  const zero = new Date(0);
  zero.setHours(0, 0, 0, 0);

  const D = new Date(0);
  D.setHours(hh, mm, ss, 0);

  return (D - zero) / 1000;
}

/**
 * Update (delete or add) Vue router query params
 * @param {object} route
 * @param {object} router
 * @param {string} queryName
 * @param {string} queryValue
 */
export function updateRouteQuery(route, router, queryName, queryValue) {
  const routParams = {...route.query};
  queryValue ? (routParams[queryName] = String(queryValue)) : (delete routParams[queryName]);

  !_.isEqual(route.query, routParams) &&
  router.push({query: routParams}).catch((err) => {
    console.log('err', err);
  });
}

/**
 * get prop from array of objects recursively;
 * @param {array} array
 * @param {string} childPropName
 * @param {string} searchPropName
 * @return {string[]}
 */
export function recursivelyGetProps(array, childPropName = 'children', searchPropName= 'id') {
  return array.reduce((total, next) => {
    const arr = [];
    next[searchPropName] && arr.push(next[searchPropName]);
    next[childPropName] && arr.push(...recursivelyGetProps(
        next[childPropName],
        childPropName,
        searchPropName,
    ));
    total.push(...arr);
    return total;
  }, []);
}

/**
 * create a recursive Object with the key = {searchPropName} and value = item;
 * @param {array} arr
 * @param {string} childPropName - field with nested values
 * @param {string} searchPropName - future key for the object
 * @param {boolean} withParentArray - must the parent id's list be included to current item data?
 * @param {array} parentArray - array with parent id's list to current item data
 * @return {{}}
 */
export function generateIdsObj(
    arr,
    childPropName = 'children',
    searchPropName = 'id',
    withParentArray = false,
    parentArray = [],
) {
  const data = {};

  arr.forEach((category) => {
    data[category[searchPropName]] = {
      ...category,
      ...(withParentArray ? {parentArray} : {}),
    };
    if (category[childPropName]?.length) {
      Object.assign(
          data,
          generateIdsObj(
              category[childPropName],
              childPropName,
              searchPropName,
              withParentArray,
              [...Object.assign([], parentArray, [category.id])],
          ),
      );
    }
  });
  return data;
}

/**
 * @return {string}
 */
export function generateUUID() {
  const timestamp = new Date().valueOf();
  return ('xxxxxxxx-xxxx-xxxx-' + timestamp).replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Dispatch custom event to window.document
 * @param {string} eventName
 * @param {object} detail
 * @return {void}
 */
export function dispatchCustomEvent(eventName, detail = {}) {
  window.document.dispatchEvent(
      new CustomEvent(eventName, {detail}),
  );
}

/**
 * Trigger api key error
 * @param {boolean} bool
 * @param {string} url
 */
export function apiKeyErrorEvent(bool, url) {
  dispatchCustomEvent(CUSTOM_EVENTS.API_KEY, {
    error: bool,
    url,
  });
}

/**
 * Retrieves a nested value from an object based on a string path.
 *
 * @param {string} strPath - The string path to the nested value, with keys separated by dots.
 * @param {Object} object - The object from which to retrieve the nested value.
 * @return {*} - The value at the specified path, or undefined if the path does not exist.
 */
export function getNestedValue(strPath, object) {
  return strPath.split('.').reduce((acc, key) => acc?.[key], object);
}

/**
 * This function combines fields by a specified name from an array of objects.
 * It creates a new object for each unique name and combines the specified fields
 * into an array.eslint-disable-next-line
 *
 * @param {Array.<Object>} initArray - The initial array of objects to process.
 * Default is an empty array.
 * @param {string} resultFieldName - The name of the field in the resulting objects where
 * the combined fields will be stored.
 * @param {Array.<string>} concatFields - An array of field names to combine into the result field.
 * @param {string} concatObjectByFieldName - The name of the field in the initial objects
 * to use for combining. The default is 'name'.
 * @return {Array.<Object>} An array of objects with combined fields.
 */
export function combineFieldsByName({
  initArray = [],
  resultFieldName,
  concatFields = [],
  concatObjectByFieldName = 'name',
}) {
  const mapByName = {};
  const unmerged = [];

  initArray.forEach((item) => {
    const concatField = item[concatObjectByFieldName];
    if (!concatField) {
      return unmerged.push(item);
    }

    if (!mapByName[concatField]) {
      mapByName[concatField] = {
        ...item,
        [resultFieldName]: [],
      };
    }
    const obj = {};
    (concatFields.length? concatFields : Object.keys(item)).forEach((field) => {
      obj[field] = item[field];
      if (item['className']) {
        mapByName[concatField]['className'] = item['className'];
      }
    });

    Object.keys(obj).length && mapByName[concatField][resultFieldName].push(obj);
  });
  return Object.values(mapByName).concat(unmerged);
}

/**
 * Filters out entries from an object where the values are falsy.
 *
 * @param {Object} obj - The object to filter.
 * @return {Object} - A new object containing only the entries with truthy values.
 */
export function filterEmptyValues(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([_, value]) => value));
}

export const caret = {
  getPosition(el) {
    return el.selectionStart;
  },
  setPosition(el, pos) {
    el.setSelectionRange(pos, pos);
  },
};

/**
 * Generates a random integer between the specified minimum and maximum values.
 *
 * @param {number} min - The minimum value (inclusive).
 * @param {number} max - The maximum value (exclusive).
 * @return {number} - A random integer between min (inclusive) and max (exclusive).
 */
export function getRandomInt(min, max) {
  const minCeiled = Math.ceil(min);
  const maxFloored = Math.floor(max);
  return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled);
}

/**
 * Check is the objects are equal to each other
 * @param {any} a - prop for compare
 * @param {any} b - prop for compare
 * @return {boolean}
 */
export function isEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}


/**
 * Filter a list of items by a specific field and value.
 * @param {Object} params - The parameters.
 * @param {Array} [params.list=[]] - The list of items to filter.
 * @param {string} [params.fieldNane='tag'] - The name of the field to filter by.
 * @param {string} [params.value=''] - The value to filter by.
 * @return {Array} - The filtered list of items.
 */
export function filterByField({list = [], fieldNane = 'tag', value}) {
  return list.filter((item) => String(item[fieldNane]) === String(value));
}


/**
 * Get attributes from the query
 * @param {object} query
 * @return {{}}
 */
export function getAttributesFromQuery(query) {
  const map = {};
  if (!query) return map;

  Object.entries(query || {}).forEach(([key, value]) => {
    const alias = key.match(/attribute\[(.*)]/)?.[1];
    alias && (map[alias] = value);
  });
  return map;
}

/**
 * Get ui block building props from settings
 * @param {string} section
 * @param {string} type
 * @param {string|number} value
 * @return {*}
 */
export function getBuilderBlockRawData(section, type, value) {
  return channel.state.settings.ui_builder?.[section]?.find((item) => {
    if (type === HOME_TYPES_NAMES.ATTRIBUTES) {
      return item.type === type && String(item.value) === String(value);
    }
    return item.type === type;
  });
}

/**
 * get prepared name to use in route alias
 * @param {object} block
 * @return {string | undefined}
 */
export function getNameFromBuilderBlock(block) {
  return block?.customName.replace(' ', '_').toLowerCase();
}

/**
 * Convert object to css string
 * @param {object} obj
 * @return {string}
 */
export function cssStringFromObject(obj) {
  return Object.entries(obj).reduce((total, [key, value]) => {
    return `${total}${key}:${value};`;
  }, '');
}

/**
 * Generate video title
 * @param {object} event
 * @return {string}
 */
export function getVideoTitle(event) {
  if (!event) {
    return '';
  }
  const {awayTeam, homeTeam} = eventTeams(event);
  if (event.participants?.length === 2) {
    return `${teamName(awayTeam.name)} @ ${teamName(homeTeam.name)}`;
  }
  return event.name || '';
}

/**
 * Get event date string
 * @param {object} event
 * @return {string}
 */
export function getVideoDate(event) {
  const {month, day, hh, zone} = eventTimeObject(event);
  return `${month} ${day} ${hh} ${zone}`;
}

/**
 * Get the name from an object with locales
 * @param {object} lang
 * @return {string}
 */
export function nameFromLocalesObject(lang) {
  if (!Object.values(lang || {}).length) return '';
  const langObj = Object.fromEntries(
      Object.entries(lang || {})
          .map(([key, value]) => [key.toLowerCase(), value]),
  );
  const name = langObj?.[Vue.i18n.locale().toLowerCase()] || langObj?.en;
  return parseStringBySeparatorToHtml(name, '\\r\\n', 'span');
}
